import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import

const API_URL = '/api/masters/drop-off-locations/';

const getAuthHeaders = (isFormData = false) => {
    const headers = { 'Authorization': `Bearer ${localStorage.getItem('kk_token')}` };
    if (!isFormData) {
        headers['Content-Type'] = 'application/json';
    }
    return headers;
};

export default function DropOffLocations() {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [newLocationName, setNewLocationName] = useState('');
    const [newLocationShortName, setNewLocationShortName] = useState('');
    
    // State for inline editing
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [editingShortName, setEditingShortName] = useState('');
    const { hasPermission } = usePermissions(); // 2. Get hook

    const fetchLocations = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(API_URL, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                setLocations(data.results || data);
            } else {
                toast.error('Failed to fetch drop-off locations.');
            }
        } catch (e) {
            toast.error('An error occurred while fetching locations.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLocations();
    }, [fetchLocations]);

    const handleAddLocation = async (e) => {
        e.preventDefault();
        if (!newLocationName.trim()) {
            toast.warn('Location name cannot be empty.');
            return;
        }
        setSaving(true);
        try {
            const res = await fetch(API_URL, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ 
                    name: newLocationName.trim().toUpperCase(),
                    short_name: newLocationShortName.trim().toUpperCase() 
                }),
            });
            if (res.ok) {
                toast.success(`Location "${newLocationName.trim().toUpperCase()}" added.`);
                setNewLocationName('');
                setNewLocationShortName('');
                fetchLocations(); // Refresh the list
            } else {
                const errorData = await res.json();
                toast.error(errorData.name?.[0] || 'Failed to add location.');
            }
        } catch (e) {
            toast.error('An error occurred while adding the location.');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteLocation = async (locationId) => {
        if (window.confirm('Are you sure you want to delete this location?')) {
            try {
                const res = await fetch(`${API_URL}${locationId}/`, {
                    method: 'DELETE',
                    headers: getAuthHeaders(),
                });
                if (res.ok) {
                    toast.success('Location deleted.');
                    fetchLocations(); // Refresh the list
                } else {
                    toast.error('Failed to delete location. It might be in use.');
                }
            } catch (e) {
                toast.error('An error occurred while deleting the location.');
            }
        }
    };

    const startEditing = (location) => {
        setEditingId(location.id);
        setEditingName(location.name);
        setEditingShortName(location.short_name || '');
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditingName('');
        setEditingShortName('');
    };

    const handleUpdateLocation = async (locationId) => {
        if (!editingName.trim()) {
            toast.warn('Location name cannot be empty.');
            return;
        }
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}${locationId}/`, {
                method: 'PATCH', // PATCH is good for partial updates
                headers: getAuthHeaders(),
                body: JSON.stringify({ 
                    name: editingName.trim().toUpperCase(),
                    short_name: editingShortName.trim().toUpperCase()
                }),
            });
            if (res.ok) {
                toast.success('Location updated successfully!');
                cancelEditing();
                fetchLocations(); // Refresh the list
            } else {
                const errorData = await res.json();
                toast.error(errorData.name?.[0] || 'Failed to update location.');
            }
        } catch (e) {
            toast.error('An error occurred while updating.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ padding: '20px', maxWidth: '800px', margin: 'auto' }}>
            <h2>Manage Drop-Off Locations</h2>

            {/* 3. Protect Add form */}
            {hasPermission('DROP OFF LOCATIONS', 'can_create') && (
                <div style={{ marginBottom: '20px', background: '#f9f9f9', padding: '15px', borderRadius: '8px' }}>
                    <h4>Add New Location</h4>
                    <form onSubmit={handleAddLocation} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div className="section-content" style={{ display: 'block' }}>
                            <div className="row" style={{ gridTemplateColumns: '2fr 1fr 1.5fr', alignItems: 'flex-end' }}>
                                <div>
                                    <input
                                        type="text"
                                        value={newLocationName}
                                        onChange={(e) => setNewLocationName(e.target.value)}
                                        placeholder="Enter new location name"
                                        disabled={saving}
                                    />
                                </div>
                                <div>
                                    <input
                                        type="text"
                                        value={newLocationShortName}
                                        onChange={(e) => setNewLocationShortName(e.target.value)}
                                        placeholder="Enter short name (optional)"
                                        disabled={saving}
                                    />
                                </div>
                                <div>
                                    <button type="submit" className="add-btn" disabled={saving} style={{ alignSelf: 'flex-start' }}>
                                        {saving ? 'Adding...' : 'Add Location'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            )}

            {loading ? (
                <div>Loading...</div>
            ) : (
                <table className="jobs-table">
                    <thead>
                        <tr>
                            <th>Location Name</th>
                            <th>Short Name</th>
                            {/* 4. Protect Actions column header */}
                            {(hasPermission('DROP OFF LOCATIONS', 'can_edit') || hasPermission('DROP OFF LOCATIONS', 'can_delete')) && (
                                <th style={{ width: '150px' }}>Actions</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {locations.map((loc) => (
                            <tr key={loc.id}>
                                {editingId === loc.id ? (
                                    <>
                                        <td>
                                            <input
                                                type="text"
                                                value={editingName}
                                                onChange={(e) => setEditingName(e.target.value)}
                                                className="search-input"
                                                autoFocus
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                value={editingShortName}
                                                onChange={(e) => setEditingShortName(e.target.value)}
                                                className="search-input"
                                            />
                                        </td>
                                        <td>
                                            {/* 5. Protect Save button */}
                                            {hasPermission('DROP OFF LOCATIONS', 'can_edit') && (
                                                <button onClick={() => handleUpdateLocation(loc.id)} className="add-btn" disabled={saving}>Save</button>
                                            )}
                                            <button onClick={cancelEditing} className="cancel-btn" style={{ marginLeft: '8px' }}>Cancel</button>
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td>{loc.name}</td>
                                        <td>{loc.short_name || '-'}</td>
                                        {/* 6. Protect Actions cell */}
                                        {(hasPermission('DROP OFF LOCATIONS', 'can_edit') || hasPermission('DROP OFF LOCATIONS', 'can_delete')) && (
                                            <td>
                                                {hasPermission('DROP OFF LOCATIONS', 'can_edit') && (
                                                    <button onClick={() => startEditing(loc)} className="add-btn" style={{ background: '#007bff' }}>Edit</button>
                                                )}
                                                {hasPermission('DROP OFF LOCATIONS', 'can_delete') && (
                                                    <button onClick={() => handleDeleteLocation(loc.id)} className="cancel-btn" style={{ marginLeft: '8px' }}>Delete</button>
                                                )}
                                            </td>
                                        )}
                                    </>
                                )}
                            </tr>
                        ))}
                        {locations.length === 0 && (
                            <tr>
                                <td colSpan="3" className="no-data">No locations found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}
        </div>
    );
}